import { useState } from "react";
import { useRenderProgress } from "@/hooks/useRenderProgress";
import { downloadRender } from "@/api/renders";

interface RenderProgressProps {
  jobId: string;
  /** Called when the user explicitly dismisses the progress widget. */
  onDismiss?: () => void;
}

export function RenderProgress({ jobId, onDismiss }: RenderProgressProps) {
  const { status, isComplete, isFailed, error, refetch, progress } = useRenderProgress(jobId);

  // Log status changes to help diagnose stuck progress
  if (typeof window !== "undefined") {
    console.debug("[RenderProgress]", { jobId, status, isComplete, isFailed });
  }
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  async function handleDownload() {
    setDownloadError(null);
    setDownloading(true);
    try {
      await downloadRender(jobId);
      // Auto-dismiss after successful download — no reason to keep
      // the progress widget open once the user has the file.
      onDismiss?.();
    } catch (err) {
      setDownloadError(err instanceof Error ? err.message : "Download failed");
    } finally {
      setDownloading(false);
    }
  }

  // The server stores progress as a 0–1 fraction; everything here is percent.
  const pct = Math.round(progress * 100);
  // Use real progress from backend if available, else fall back to indeterminate
  const barWidth = isComplete
    ? 100
    : isFailed
      ? 100
      : pct > 0
        ? pct
        : status === "rendering"
          ? 15
          : 5;

  return (
    <div className="rounded-xl border border-line bg-surface-2 p-3">
      <div className="mb-2 flex items-center justify-between gap-3">
        <span className="text-xs font-medium text-ink-2">
          {status === "queued" && "Queued..."}
          {status === "rendering" && `Rendering... ${pct}%`}
          {status === "complete" && "Complete"}
          {status === "error" && "Failed"}
          {status === null && "Loading..."}
        </span>
        <div className="flex items-center gap-2">
          {!isComplete && !isFailed && (
            <button
              type="button"
              onClick={() => refetch()}
              className="text-xs text-ink-3 hover:text-ink"
              title="Refresh status"
            >
              ↻
            </button>
          )}
          {isComplete && (
            <button
              type="button"
              onClick={handleDownload}
              disabled={downloading}
              className="rounded-lg bg-accent px-3 py-1 text-xs font-medium text-accent-fg transition-colors hover:bg-accent-hover disabled:opacity-40"
            >
              {downloading ? "Downloading..." : "Download"}
            </button>
          )}
          {onDismiss && (
            <button
              type="button"
              onClick={onDismiss}
              aria-label="Dismiss"
              className="text-sm text-ink-3 hover:text-ink"
            >
              ×
            </button>
          )}
        </div>
      </div>

      <div className="h-1.5 w-full overflow-hidden rounded-full bg-surface-3">
        <div
          className={`h-full rounded-full transition-all duration-300 ${
            isFailed
              ? "bg-danger"
              : isComplete
                ? "bg-success"
                : "bg-accent"
          }`}
          style={{ width: `${barWidth}%` }}
        />
      </div>

      {error && (
        <p className="mt-2 text-xs text-danger">{error}</p>
      )}
      {downloadError && (
        <p className="mt-2 text-xs text-danger">{downloadError}</p>
      )}
    </div>
  );
}
